import { useEffect, useRef, type CSSProperties } from 'react';

const milestones = [
  { number: '01', title: 'Discover', description: 'Understand your goals, scope, and priorities.' },
  { number: '02', title: 'Assess', description: 'Identify risks, requirements, and opportunities.' },
  { number: '03', title: 'Build', description: 'Test or develop within the agreed scope.' },
  { number: '04', title: 'Improve', description: 'Deliver actionable next steps and refinements.' },
] as const;

export default function MilestoneTimeline() {
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = timelineRef.current;
    if (!element || !('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      element.classList.add('is-revealed');
      observer.disconnect();
    }, { threshold: 0.12, rootMargin: '0px 0px -32px 0px' });

    // Add the animation gate only when the section is below the initial viewport.
    // Existing visible content remains readable on slow connections or without JS.
    if (element.getBoundingClientRect().top > window.innerHeight * 0.86) {
      element.classList.add('can-reveal');
    }
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="hp-milestones" ref={timelineRef}>
      <div className="hp-milestone-line" aria-hidden="true" />
      {milestones.map((item, index) => (
        <article key={item.number} className="hp-milestone" style={{ '--step-index': index } as CSSProperties}>
          <div className="hp-milestone-node" aria-hidden="true" />
          <div className="hp-milestone-meta">{item.number}</div>
          <h3>{item.title}</h3>
          <p>{item.description}</p>
        </article>
      ))}
    </div>
  );
}
